import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Video, Home, Building2, Leaf, Sun, MapPin, Calendar, Monitor } from "lucide-react";
import type { BookingData } from "../BookingModal";

interface TimeSelectionProps {
  bookingData: BookingData;
  setBookingData: (data: BookingData) => void;
  onNext: () => void;
  onBack?: () => void;
}

interface SlotWithLocation {
  time: string;
  location_id: string | null;
  location_name: string | null;
  location_city: string | null;
  symbolIndex: number;
  hasVisio: boolean;
  hasCabinet: boolean;
}

// Symboles pour les lieux (correspondance avec DateSelection)
const LOCATION_SYMBOLS = [
  { icon: Home, class: "text-primary" },
  { icon: Building2, class: "text-secondary" },
  { icon: Leaf, class: "text-accent" },
  { icon: Sun, class: "text-muted-foreground" },
  { icon: MapPin, class: "text-foreground" },
];

// Filter out past time slots if the selected date is today
const filterPastSlotsForToday = (selectedDate: Date, slots: SlotWithLocation[]): SlotWithLocation[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selected = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
  
  if (selected.getTime() !== today.getTime()) return slots;
  
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  return slots.filter(slot => {
    const [h, m] = slot.time.split(':').map(Number);
    return h * 60 + m > currentHour * 60 + currentMinute;
  });
};

const generateTimeSlots = (startHour: number, endHour: number): string[] => {
  const slots: string[] = [];
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${String(hour).padStart(2, '0')}:00`);
  }
  return slots;
};

// Map pour stocker l'index des symboles par lieu
const locationSymbolMap = new Map<string, number>();

export function TimeSelection({ bookingData, setBookingData, onNext, onBack }: TimeSelectionProps) {
  const [availableSlots, setAvailableSlots] = useState<SlotWithLocation[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<SlotWithLocation | null>(
    bookingData.time ? { time: bookingData.time, location_id: null, location_name: null, location_city: null, symbolIndex: 0, hasVisio: false, hasCabinet: false } : null
  );
  const [loading, setLoading] = useState(true);
  const [noSlotsMessage, setNoSlotsMessage] = useState<string>('');
  const [locationSymbols, setLocationSymbols] = useState<Map<string, number>>(new Map());
  const [homeLocationIds, setHomeLocationIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (bookingData.date) {
      loadLocationsAndSlots();
    }
  }, [bookingData.date, bookingData.isOnline, bookingData.isHomeVisit]);

  const loadLocationsAndSlots = async () => {
    // Charger les lieux avec le même tri que useCalendarLocationSymbols pour cohérence des symboles
    const { data: locationsData } = await supabase
      .from("locations")
      .select("id, name, is_default, is_home_visit")
      .eq("is_active", true)
      .order("is_default", { ascending: false })
      .order("name", { ascending: true });

    const rows = (locationsData as any[]) || [];
    const homeIds = new Set<string>(rows.filter((l) => l.is_home_visit === true).map((l) => l.id));
    setHomeLocationIds(homeIds);

    const symbolMap = new Map<string, number>();
    rows
      .filter((loc) => loc.is_home_visit !== true)
      .forEach((loc, index) => {
        symbolMap.set(loc.id, index % LOCATION_SYMBOLS.length);
      });
    setLocationSymbols(symbolMap);

    // Then load slots
    await loadAvailableSlots(symbolMap, homeIds);
  };

  const loadAvailableSlots = async (symbolMap?: Map<string, number>, homeIds?: Set<string>) => {
    if (!bookingData.date) return;
    const symbols = symbolMap || locationSymbols;
    const homeLocs = homeIds || homeLocationIds;
    // Un créneau sans lieu précisé reste un créneau cabinet ; seuls les lieux
    // marqués « à domicile » sont exclus du mode présentiel.
    const isCabinetLocation = (id: string | null | undefined) => !id || !homeLocs.has(id);

    
    setLoading(true);
    setNoSlotsMessage('');

    try {
      const dayOfWeek = bookingData.date.getDay();
      const year = bookingData.date.getFullYear();
      const month = String(bookingData.date.getMonth() + 1).padStart(2, '0');
      const day = String(bookingData.date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      // Get practitioner info
      const { data: practitionerInfo } = await supabase
        .rpc('get_public_practitioner_info')
        .limit(1)
        .single() as { data: { id: string; user_id: string; business_name: string; practitioner_name: string; address: string } | null };

      if (!practitionerInfo?.user_id) {
        const defaultSlots = generateTimeSlots(9, 18);
        await checkBookedSlots(dateStr, defaultSlots.map(t => ({ time: t, location_id: null, location_name: null, location_city: null, symbolIndex: 0, hasVisio: true, hasCabinet: false })));
        return;
      }

      // Try to get availability with location info (weekly overrides first)
      const { data: weeklyOverrides } = await supabase
        .rpc('get_public_availability_with_location', { p_date: dateStr });

      if (weeklyOverrides && weeklyOverrides.length > 0) {
        // Filter only available slots
        const availableOverrides = weeklyOverrides.filter((o: any) => o.is_available);
        
        if (availableOverrides.length === 0) {
          setAvailableSlots([]);
          setNoSlotsMessage('Le cabinet est fermé ce jour-là');
          setLoading(false);
          return;
        }

        // Check online/office compatibility - STRICT by selected mode
        const compatibleOverrides = availableOverrides.filter((override: any) => {
          if (bookingData.isHomeVisit) return override.home_visit === true;
          if (bookingData.isOnline && override.in_office_only) return false;
          if (!bookingData.isOnline && override.online_only) return false;
          return true;
        });

        if (compatibleOverrides.length === 0) {
          setAvailableSlots([]);
          setNoSlotsMessage(
            bookingData.isHomeVisit
              ? "Aucun créneau à domicile ce jour-là"
              : bookingData.isOnline
              ? "Aucun créneau en visio ce jour-là"
              : "Aucun créneau en présentiel ce jour-là"
          );
          setLoading(false);
          return;
        }


        // Generate slots with location info and symbols - accumulate modes per time
        const slotMap = new Map<string, SlotWithLocation>();
        compatibleOverrides.forEach((override: any) => {
          const startHour = parseInt(override.start_time.split(':')[0]);
          const endHour = parseInt(override.end_time.split(':')[0]);
          // Determine if this slot supports visio and/or cabinet
          const supportsVisio = override.online_only === true || override.in_office_only !== true;
          const supportsCabinet = override.in_office_only === true
            ? isCabinetLocation(override.location_id)
            : override.online_only !== true && isCabinetLocation(override.location_id);
          
          for (let hour = startHour; hour < endHour; hour++) {
            const timeKey = `${String(hour).padStart(2, '0')}:00`;
            const existing = slotMap.get(timeKey);
            const locationId = override.location_id || null;
            
            if (existing) {
              // Merge modes
              existing.hasVisio = existing.hasVisio || supportsVisio;
              existing.hasCabinet = existing.hasCabinet || supportsCabinet;
              // Keep location info if cabinet is supported
              if (supportsCabinet && locationId) {
                existing.location_id = locationId;
                existing.location_name = override.location_name || null;
                existing.location_city = override.location_city || null;
                existing.symbolIndex = symbols.get(locationId) ?? 0;
              }
            } else {
              slotMap.set(timeKey, {
                time: timeKey,
                location_id: supportsCabinet ? locationId : null,
                location_name: supportsCabinet ? (override.location_name || null) : null,
                location_city: supportsCabinet ? (override.location_city || null) : null,
                symbolIndex: locationId ? (symbols.get(locationId) ?? 0) : 0,
                hasVisio: supportsVisio,
                hasCabinet: supportsCabinet,
              });
            }
          }
        });

      // Sort by time and filter past slots for today
        let uniqueSlots = Array.from(slotMap.values()).sort((a, b) => a.time.localeCompare(b.time));
        uniqueSlots = filterPastSlotsForToday(bookingData.date!, uniqueSlots);
        if (!bookingData.isHomeVisit) {
          uniqueSlots = uniqueSlots.filter((s) => (bookingData.isOnline ? s.hasVisio : s.hasCabinet));
        }

        await checkBookedSlots(dateStr, uniqueSlots);
        return;
      }

      // Fall back to default availability with location
      const { data: defaultAvailability } = await supabase
        .rpc('get_default_availability_with_location', { p_day_of_week: dayOfWeek });

      if (!defaultAvailability || defaultAvailability.length === 0) {
        setAvailableSlots([]);
        setNoSlotsMessage('Le cabinet est fermé ce jour-là');
        setLoading(false);
        return;
      }

      // Check online/office compatibility - STRICT by selected mode
      const compatibleSettings = defaultAvailability.filter((setting: any) => {
        if (bookingData.isHomeVisit) return setting.home_visit === true;
        if (bookingData.isOnline && setting.in_office_only) return false;
        if (!bookingData.isOnline && setting.online_only) return false;
        return true;
      });

      if (compatibleSettings.length === 0) {
        setAvailableSlots([]);
        setNoSlotsMessage(
          bookingData.isHomeVisit
            ? "Aucun créneau à domicile ce jour-là"
            : bookingData.isOnline
            ? "Aucun créneau en visio ce jour-là"
            : "Aucun créneau en présentiel ce jour-là"
        );

        setLoading(false);
        return;
      }

      // Generate slots with location info and symbols - accumulate modes per time
      const slotMap = new Map<string, SlotWithLocation>();
      compatibleSettings.forEach((setting: any) => {
        const normalizedStartTime = setting.start_time.substring(0, 5);
        const normalizedEndTime = setting.end_time.substring(0, 5);
        const startHour = parseInt(normalizedStartTime.split(':')[0]);
        const endHour = parseInt(normalizedEndTime.split(':')[0]);
        // Determine if this slot supports visio and/or cabinet
        const supportsVisio = setting.online_only === true || setting.in_office_only !== true;
        const supportsCabinet = setting.in_office_only === true
          ? isCabinetLocation(setting.location_id)
          : setting.online_only !== true && isCabinetLocation(setting.location_id);
        
        for (let hour = startHour; hour < endHour; hour++) {
          const timeKey = `${String(hour).padStart(2, '0')}:00`;
          const existing = slotMap.get(timeKey);
          const locationId = setting.location_id || null;
          
          if (existing) {
            // Merge modes
            existing.hasVisio = existing.hasVisio || supportsVisio;
            existing.hasCabinet = existing.hasCabinet || supportsCabinet;
            // Keep location info if cabinet is supported
            if (supportsCabinet && locationId) {
              existing.location_id = locationId;
              existing.location_name = setting.location_name || null;
              existing.location_city = setting.location_city || null;
              existing.symbolIndex = symbols.get(locationId) ?? 0;
            }
          } else {
            slotMap.set(timeKey, {
              time: timeKey,
              location_id: supportsCabinet ? locationId : null,
              location_name: supportsCabinet ? (setting.location_name || null) : null,
              location_city: supportsCabinet ? (setting.location_city || null) : null,
              symbolIndex: locationId ? (symbols.get(locationId) ?? 0) : 0,
              hasVisio: supportsVisio,
              hasCabinet: supportsCabinet,
            });
          }
        }
      });

      // Sort by time and filter past slots for today
      let uniqueSlots = Array.from(slotMap.values()).sort((a, b) => a.time.localeCompare(b.time));
      uniqueSlots = filterPastSlotsForToday(bookingData.date!, uniqueSlots);
      if (!bookingData.isHomeVisit) {
        uniqueSlots = uniqueSlots.filter((s) => (bookingData.isOnline ? s.hasVisio : s.hasCabinet));
      }

      await checkBookedSlots(dateStr, uniqueSlots);

    } catch (error) {
      console.error('Error in loadAvailableSlots:', error);
      setAvailableSlots([]);
      setLoading(false);
    }
  };

  const checkBookedSlots = async (dateStr: string, allSlots: SlotWithLocation[]) => {
    try {
      const { data: allAppointments, error } = await supabase
        .from('appointments')
        .select('appointment_date, appointment_time, duration_minutes, status, is_online, first_name, last_name')
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching appointments:', error);
        setAvailableSlots(allSlots);
        setLoading(false);
        return;
      }

      // Filter appointments on the selected date
      const relevantAppointments = allAppointments?.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        const aptYear = aptDate.getFullYear();
        const aptMonth = String(aptDate.getMonth() + 1).padStart(2, '0');
        const aptDay = String(aptDate.getDate()).padStart(2, '0');
        const aptDateStr = `${aptYear}-${aptMonth}-${aptDay}`;
        return aptDateStr === dateStr;
      }) || [];

      // Filter slots that overlap with existing appointments
      const available = allSlots.filter(slot => {
        const [slotHourStr] = slot.time.split(':');
        const slotHour = parseInt(slotHourStr);
        
        const hasConflict = relevantAppointments.some(apt => {
          const aptDate = new Date(apt.appointment_date);
          const aptHour = aptDate.getHours();
          const aptMinute = aptDate.getMinutes();
          
          const aptStartMinutes = aptHour * 60 + aptMinute;
          const aptEndMinutes = aptStartMinutes + (apt.duration_minutes || 60);
          
          const slotStartMinutes = slotHour * 60;
          const slotEndMinutes = slotStartMinutes + 60;
          
          return (slotStartMinutes < aptEndMinutes && slotEndMinutes > aptStartMinutes);
        });
        
        return !hasConflict;
      });
      
      setAvailableSlots(available);
      if (available.length === 0 && !noSlotsMessage) {
        setNoSlotsMessage('Tous les créneaux sont réservés pour ce jour');
      }
    } catch (error) {
      console.error('Error in checkBookedSlots:', error);
      setAvailableSlots(allSlots);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot: SlotWithLocation) => {
    setSelectedSlot(slot);

    // Strict mode behavior:
    // - Visio booking => no cabinet location stored
    // - Présentiel booking => store cabinet location
    if (bookingData.isOnline || bookingData.isHomeVisit) {
      setBookingData({
        ...bookingData,
        time: slot.time,
        locationId: null,
        locationName: null,
        locationCity: null,
      });
    } else {
      setBookingData({
        ...bookingData,
        time: slot.time,
        locationId: slot.location_id,
        locationName: slot.location_name,
        locationCity: slot.location_city,
      });
    }
  };

  const handleNext = () => {
    if (selectedSlot) {
      onNext();
    }
  };

  const handleChangeDate = () => {
    // Reset time selection and go back to date
    setBookingData({
      ...bookingData,
      time: null,
      locationId: null,
      locationName: null,
      locationCity: null,
    });
    setSelectedSlot(null);
    onBack?.();
  };

  // Render the symbol for a location
  const renderLocationSymbol = (symbolIndex: number, className?: string) => {
    const config = LOCATION_SYMBOLS[symbolIndex % LOCATION_SYMBOLS.length];
    const IconComponent = config.icon;
    return <IconComponent className={`w-4 h-4 ${config.class} ${className || ''}`} />;
  };

  if (!bookingData.date) {
    return <div>Veuillez d'abord sélectionner une date</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">
          Choisissez votre créneau
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-muted-foreground">
            {bookingData.date
              ? `Créneaux disponibles pour le ${format(bookingData.date, 'dd MMMM yyyy', { locale: fr })}`
              : 'Sélectionnez une date d\'abord'}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleChangeDate}
            className="gap-2 w-fit"
          >
            <Calendar className="w-4 h-4" />
            Changer de date
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">
          Chargement des créneaux disponibles...
        </div>
      ) : noSlotsMessage ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">{noSlotsMessage}</p>
          <Button variant="outline" onClick={handleChangeDate}>
            Choisir une autre date
          </Button>
        </div>
      ) : availableSlots.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">
            Aucun créneau disponible pour cette date
          </p>
          <Button variant="outline" onClick={handleChangeDate}>
            Choisir une autre date
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {availableSlots.map((slot) => (
            <button
              key={slot.time}
              onClick={() => handleSlotSelect(slot)}
              className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                selectedSlot?.time === slot.time
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border hover:border-primary hover:bg-accent'
              }`}
            >
              {/* Symboles pour identifier les modes disponibles */}
              <div className="absolute top-2 right-2 flex gap-1">
                {bookingData.isHomeVisit ? (
                  <Home className="w-4 h-4 text-amber-500" />
                ) : bookingData.isOnline ? (
                  slot.hasVisio && <Monitor className="w-4 h-4 text-blue-500" />
                ) : (
                  slot.hasCabinet && slot.location_id && renderLocationSymbol(slot.symbolIndex)
                )}
              </div>
              <div className="font-semibold text-lg">{slot.time}</div>
              {/* Show both modes info */}
              <div className="flex flex-col gap-0.5 mt-1">
                {bookingData.isHomeVisit && (
                  <div className={`flex items-center gap-1.5 text-xs ${
                    selectedSlot?.time === slot.time ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    <Home className="w-3 h-3 text-amber-500" />
                    <span>Consultation à domicile</span>
                  </div>
                )}
                {!bookingData.isHomeVisit && bookingData.isOnline && slot.hasVisio && (
                  <div className={`flex items-center gap-1 text-xs ${
                    selectedSlot?.time === slot.time ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    <Monitor className="w-3 h-3 text-blue-500" />
                    <span>Visio</span>
                  </div>
                )}
                {!bookingData.isHomeVisit && !bookingData.isOnline && slot.hasCabinet && slot.location_name && (
                  <div className={`flex items-center gap-1.5 text-xs ${
                    selectedSlot?.time === slot.time ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    {renderLocationSymbol(slot.symbolIndex, 'w-3 h-3')}
                    <span>{slot.location_name}{slot.location_city ? ` · ${slot.location_city}` : ''}</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedSlot && (
        <button
          onClick={handleNext}
          className="w-full p-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
        >
          Continuer
        </button>
      )}

      <div className="text-sm text-muted-foreground text-center">
        <p>Fuseau horaire</p>
        <p className="font-medium">Heure d'Europe centrale (UTC+1)</p>
      </div>
    </div>
  );
}
